/**
 * SmallGroupSlotManageDialog — comprehensive admin/staff dialog for a
 * single small-group session slot. Reached from the lane calendar by
 * clicking a small-group block.
 *
 * Features:
 *  - Slot header: session name, date, time, "X / Y filled" badge
 *  - Roster: list of registered players with parent info
 *  - Remove player (with confirm)
 *  - Replace player (pick a different player belonging to same parent)
 *  - Mark attendance: Present / Absent / Late per player
 *  - Generate Invoices: only enabled once every player has an attendance
 *    mark; calls the existing `/api/small-group/slots/{id}/complete`
 *    endpoint which writes the small_group_invoices and emails parents.
 *
 * Permissions: admin + staff. Buttons are gated by `userRole` prop.
 */
import { useEffect, useState, useMemo } from 'react';
import {
  Users,
  UserMinus,
  Repeat,
  Check,
  X,
  Clock,
  FileText,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../services/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';

const ATTENDANCE_BADGE = {
  present: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  absent:  'bg-red-100 text-red-700 border-red-200',
  late:    'bg-amber-100 text-amber-800 border-amber-200',
};

export default function SmallGroupSlotManageDialog({
  open,
  onOpenChange,
  slotId,
  onAfterChange,
  userRole = 'admin',
}) {
  const [slot, setSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyRow, setBusyRow] = useState(null);  // registration_id currently saving
  const [generating, setGenerating] = useState(false);
  // Local attendance drafts so admins can mark multiple players before
  // committing in one batch. Map<registration_id, 'present'|'absent'|'late'>
  const [drafts, setDrafts] = useState({});

  const canManage = ['admin', 'staff'].includes(userRole);

  const fetchSlot = async () => {
    if (!slotId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/small-group/slots/${slotId}`);
      setSlot(data);
      // Seed drafts from server attendance
      const seed = {};
      for (const r of data.registrations || []) {
        if (r.attendance_status) seed[r.id] = r.attendance_status;
      }
      setDrafts(seed);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load slot');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchSlot();
    else {
      setSlot(null);
      setDrafts({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slotId]);

  const registered = slot?.registrations || [];
  const allMarked = useMemo(
    () => registered.length > 0 && registered.every((r) => drafts[r.id]),
    [registered, drafts],
  );

  const handleAttendance = (regId, value) => {
    setDrafts((d) => ({ ...d, [regId]: value }));
  };

  const saveAttendance = async () => {
    if (!slot) return;
    const attendance = Object.entries(drafts)
      .filter(([, v]) => v)
      .map(([registration_id, status]) => ({
        registration_id,
        attendance_status: status,
      }));
    if (attendance.length === 0) return;
    try {
      await api.post(`/small-group/slots/${slot.id}/attendance`, { attendance });
      toast.success(`Attendance saved (${attendance.length})`);
      await fetchSlot();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save attendance');
    }
  };

  const handleRemove = async (reg) => {
    if (!window.confirm(`Remove ${reg.player_name} from this session?`)) return;
    setBusyRow(reg.id);
    try {
      await api.delete(`/small-group/admin/registrations/${reg.id}`);
      toast.success(`${reg.player_name} removed`);
      await fetchSlot();
      onAfterChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remove failed');
    } finally {
      setBusyRow(null);
    }
  };

  const handleReplace = async (reg) => {
    try {
      // Fetch this parent's other players so admin can swap one in.
      const { data } = await api.get(`/admin/parents/${reg.parent_id}/players`);
      const candidates = (data || []).filter((p) => p.id !== reg.player_id);
      if (candidates.length === 0) {
        toast.info('No other players for this parent — add a player to their profile first.');
        return;
      }
      const names = candidates.map((p, i) => `${i + 1}. ${p.full_name}`).join('\n');
      const choice = window.prompt(
        `Replace ${reg.player_name} with which player?\n\n${names}\n\nEnter number (1-${candidates.length}):`,
      );
      const idx = parseInt(choice, 10) - 1;
      if (Number.isNaN(idx) || idx < 0 || idx >= candidates.length) return;
      const newPlayer = candidates[idx];
      setBusyRow(reg.id);
      // Remove old + register new in sequence. The register endpoint
      // accepts (player_id, parent_id) for this slot.
      await api.delete(`/small-group/admin/registrations/${reg.id}`);
      await api.post('/small-group/registrations', {
        slot_id: slotId,
        player_id: newPlayer.id,
        parent_id: reg.parent_id,
      });
      toast.success(`Replaced ${reg.player_name} → ${newPlayer.full_name}`);
      await fetchSlot();
      onAfterChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Replace failed');
    } finally {
      setBusyRow(null);
    }
  };

  const handleGenerateInvoices = async () => {
    if (!allMarked) {
      toast.error('Mark attendance for every player before generating invoices.');
      return;
    }
    // Save any unsaved attendance first so the backend sees the final state.
    await saveAttendance();
    if (!window.confirm(
      'Generate invoices for all PRESENT/LATE players in this session? '
      + 'Absent players will be skipped. Each parent will receive an email.',
    )) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/small-group/slots/${slotId}/complete`);
      const created = data?.invoices_created ?? data?.invoice_count ?? 0;
      const skipped = data?.absent_skipped ?? 0;
      toast.success(`${created} invoice${created === 1 ? '' : 's'} generated · ${skipped} absent player${skipped === 1 ? '' : 's'} skipped`, { duration: 7000 });
      await fetchSlot();
      onAfterChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoices', { duration: 8000 });
    } finally {
      setGenerating(false);
    }
  };

  const completed = slot?.status === 'completed';
  const capacity = slot?.max_capacity ?? 0;
  const fillPct = capacity > 0 ? Math.min(100, ((slot?.registered_count || 0) / capacity) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="sg-slot-manage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-600" />
            {slot?.session_name || 'Small Group Session'}
            {completed && <Badge className="bg-emerald-100 text-emerald-700 ml-2">Completed</Badge>}
          </DialogTitle>
          <DialogDescription>
            {slot && (
              <span className="text-xs">
                {slot.date} · {slot.start_time}–{slot.end_time} · Coach {slot.coach_name || '—'}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading roster…
          </div>
        ) : slot ? (
          <div className="space-y-3">
            {/* Capacity bar */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Roster</span>
              <span className="font-mono text-slate-700" data-testid="sg-slot-capacity">
                {slot.registered_count} / {capacity} filled
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded overflow-hidden">
              <div
                className={`h-full ${fillPct >= 100 ? 'bg-red-500' : fillPct >= 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${fillPct}%` }}
              />
            </div>

            {registered.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-400 border border-dashed rounded-lg">
                No players registered yet.
              </div>
            ) : (
              <div className="border rounded-lg divide-y max-h-96 overflow-y-auto" data-testid="sg-roster">
                {registered.map((r) => {
                  const status = drafts[r.id];
                  return (
                    <div key={r.id} className="p-3 flex items-center justify-between gap-2" data-testid={`sg-roster-row-${r.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                          {r.player_name}
                          {status && (
                            <Badge variant="outline" className={`text-[10px] ${ATTENDANCE_BADGE[status]}`} data-testid={`sg-att-badge-${r.id}`}>
                              {status}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 truncate">{r.parent_name} · {r.parent_email}</div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        {/* Attendance trio */}
                        {!completed && canManage && (
                          <>
                            <Button
                              size="sm" variant={status === 'present' ? 'default' : 'outline'}
                              className={`h-7 px-2 text-[11px] ${status === 'present' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-emerald-700 border-emerald-200 hover:bg-emerald-50'}`}
                              onClick={() => handleAttendance(r.id, 'present')}
                              data-testid={`sg-mark-present-${r.id}`}
                            >
                              <Check className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm" variant={status === 'late' ? 'default' : 'outline'}
                              className={`h-7 px-2 text-[11px] ${status === 'late' ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'text-amber-700 border-amber-200 hover:bg-amber-50'}`}
                              onClick={() => handleAttendance(r.id, 'late')}
                              data-testid={`sg-mark-late-${r.id}`}
                            >
                              <Clock className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm" variant={status === 'absent' ? 'default' : 'outline'}
                              className={`h-7 px-2 text-[11px] ${status === 'absent' ? 'bg-red-600 hover:bg-red-700 text-white' : 'text-red-700 border-red-200 hover:bg-red-50'}`}
                              onClick={() => handleAttendance(r.id, 'absent')}
                              data-testid={`sg-mark-absent-${r.id}`}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                            <div className="w-px h-5 bg-slate-200 mx-1" />
                            <Button
                              size="sm" variant="ghost"
                              className="h-7 px-2 text-[11px] text-blue-700 hover:bg-blue-50"
                              disabled={busyRow === r.id}
                              onClick={() => handleReplace(r)}
                              title="Replace player"
                              data-testid={`sg-replace-${r.id}`}
                            >
                              <Repeat className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              className="h-7 px-2 text-[11px] text-red-700 hover:bg-red-50"
                              disabled={busyRow === r.id}
                              onClick={() => handleRemove(r)}
                              title="Remove player"
                              data-testid={`sg-remove-${r.id}`}
                            >
                              {busyRow === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserMinus className="h-3 w-3" />}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!completed && registered.length > 0 && (
              <div className="flex items-center justify-between gap-3 bg-slate-50 border rounded-lg p-3 text-xs">
                <div className="flex items-center gap-2">
                  {allMarked ? (
                    <span className="text-emerald-700 font-medium flex items-center gap-1">
                      <Check className="h-3.5 w-3.5" /> All players marked — ready to invoice
                    </span>
                  ) : (
                    <span className="text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Mark attendance for {registered.length - Object.values(drafts).filter(Boolean).length} more player(s)
                    </span>
                  )}
                </div>
                <Button
                  size="sm" variant="outline"
                  onClick={saveAttendance}
                  disabled={Object.values(drafts).filter(Boolean).length === 0}
                  data-testid="sg-save-attendance-btn"
                >
                  Save attendance
                </Button>
              </div>
            )}
          </div>
        ) : null}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>
            Close
          </Button>
          {!completed && canManage && slot && registered.length > 0 && (
            <Button
              onClick={handleGenerateInvoices}
              disabled={!allMarked || generating}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="sg-generate-invoices-btn"
            >
              {generating ? (
                <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Generating…</>
              ) : (
                <><FileText className="h-4 w-4 mr-1.5" /> Generate Invoices</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
